import { parseSchedules } from 'elements/scheduleDialog/utils'
import restClient from 'restClient'
import { urlToId } from 'util/misc'
import { API_URL } from './constants'
import type { TariffValidityPeriodFields } from './selectTariffDialog/v2/TariffSelectorListTable/types'

export interface FetchTariffByCodeOptions {
  detailed?: boolean
  fields?: string
}

const DEFAULT_FIELDS =
  'id,code,name,data,component_contents,distributor_name,utility_tariff_group,valid_from,valid_to,is_latest'

export const fetchTariffsByCodes = async (
  tariffCodes: string[],
  orgId: number,
  options: FetchTariffByCodeOptions = {}
): Promise<Map<string, any>> => {
  const { detailed = false, fields = DEFAULT_FIELDS } = options

  const restClientInstance = restClient(API_URL)

  if (tariffCodes.length === 0) {
    return new Map()
  }

  const params = new URLSearchParams({
    utility_tariff_group__code__in: tariffCodes.join(','),
    ordering: '-valid_from',
    fields,
  })

  if (detailed) {
    params.append('detailed', 'true')
  }

  try {
    const res = await restClientInstance('CUSTOM_GET', 'custom', {
      url: `${API_URL}/orgs/${orgId}/utility_tariffs/?${params}`,
    })

    const tariffMap = new Map<string, any>()
    if (res?.data) {
      res.data.forEach((tariff: any) => {
        const groupCode = tariff.utility_tariff_group__code

        if (groupCode && tariffCodes.includes(groupCode) && !tariffMap.has(groupCode)) {
          tariffMap.set(groupCode, tariff)
        }
      })
    }
    return tariffMap
  } catch (err) {
    console.warn('Failed to fetch tariffs by codes:', err)
    return new Map()
  }
}

export const createTariffRecord = (tariff: any, orgId: number) => ({
  ...tariff,
  url: `${API_URL}/orgs/${orgId}/utility_tariffs/${tariff.id}/`,
})

export const formTariffUrl = (data: string | number, orgId: number): string => {
  return `${API_URL}/orgs/${orgId}/utility_tariffs/${typeof data === 'string' ? urlToId(data) : data}/`
}

/** Parses tariff `data` JSON into schedule rows; returns null when missing or invalid. */
export function parseTariffRecordSchedules(data: string | null | undefined) {
  if (!data) {
    return null
  }

  try {
    return parseSchedules(JSON.parse(data))
  } catch {
    return null
  }
}

export function formatTariffValidityPeriod(
  row: TariffValidityPeriodFields,
  formatDate: (isoDate: string) => string,
  translate: (key: string) => string
): string {
  if (row.valid_from == null || row.valid_from === '') {
    return '-'
  }
  const from = formatDate(String(row.valid_from))
  const openEnded = row.valid_to == null || row.valid_to === ''
  const toLabel = openEnded ? translate('Current') : formatDate(String(row.valid_to))
  return `${from} - ${toLabel}`
}
